 import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
 import { ArrowRight } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
 import { supabase } from "@/integrations/supabase/client";
 import { toast } from "sonner";
 import { z } from "zod";
import { ThemedLogo } from "@/components/shared/ThemedLogo";
import { FEATURE_COSMO_CHAT } from "@/lib/featureFlags";

 const emailSchema = z.string().email("Please enter a valid email address");

const Landing = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

   const [email, setEmail] = useState("");
   const [isSubmitting, setIsSubmitting] = useState(false);

  // Redirect authenticated users to the appropriate home page
  useEffect(() => {
    if (!authLoading && user) {
      navigate(FEATURE_COSMO_CHAT ? '/cosmo' : '/prompt', { replace: true });
    }
  }, [user, authLoading, navigate]);

  // Show loading while checking auth
  if (authLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

   const handleSubmit = async (e: React.FormEvent) => {
     e.preventDefault();
     
     const validation = emailSchema.safeParse(email.trim());
     if (!validation.success) {
       toast.error(validation.error.errors[0].message);
       return;
     }
 
     setIsSubmitting(true);
     try {
       const { error } = await supabase
         .from('invite_requests')
         .insert({ email: email.trim().toLowerCase() });
 
       if (error) {
         if (error.code === '23505') {
           toast.error("This email has already been submitted");
         } else {
           throw error;
         }
       } else {
         toast.success("Thanks! We'll be in touch soon.");
         setEmail("");
       }
     } catch (error) {
       console.error('Error submitting invite request:', error);
       toast.error("Something went wrong. Please try again.");
     } finally {
       setIsSubmitting(false);
     }
   };
 
  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center px-6 gap-6">
      <Card className="max-w-md w-full animate-fade-in">
        <CardContent className="pt-6 text-center">
          <ThemedLogo className="h-24 md:h-28 mx-auto mb-6" />
          <p className="text-base text-muted-foreground mb-8">
            Prompt better, remix what works, build a shared library, and track real progress across your organization.
          </p>
          
          <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-2">
            <Input
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="flex-1 bg-muted"
              disabled={isSubmitting}
            />
            <Button type="submit" disabled={isSubmitting} className="w-full sm:w-auto">
              {isSubmitting ? "Submitting..." : "Request an invite"}
            </Button>
          </form>
        </CardContent>
      </Card>

      <button
        onClick={() => navigate("/login")}
        className="text-sm text-muted-foreground hover:text-foreground transition-colors inline-flex items-center gap-1"
      >
        Sign in
        <ArrowRight className="ml-2 h-4 w-4" />
      </button>
    </div>
  );
};

export default Landing;
